<template>
  <div class="kit">
    <header class="kit__head">
      <div>
        <div class="kit__eyebrow">Phase 0 · dev harness</div>
        <h1 class="kit__title">Hermes shared kit</h1>
        <p class="kit__sub">Every Phase 0 component with sample data. Dev-only preview — reachable via <code>?kit=1</code>. Toggle the theme to verify dark-mode tokens.</p>
      </div>
      <div class="kit__chrome">
        <HermesTenantSwitcher />
        <HermesThemeToggle />
      </div>
    </header>

    <HermesBreadcrumbs :items="crumbs" />

    <!-- Stat tiles -->
    <h2 class="kit__sec">Stat tiles</h2>
    <div class="kit__grid kit__grid--4">
      <HermesStatTile label="MRR" :value="48200" currency="GBP" :delta="6.4" delta-text="6.4%" caption="vs last month" :sparkline="[31,33,34,39,41,45,48]" />
      <HermesStatTile label="Active subscriptions" :value="214" :delta="11" delta-text="11" caption="net this month" :sparkline="[180,185,190,188,199,205,214]" />
      <HermesStatTile label="Failed payments" :value="7" :delta="3" delta-text="3" good-direction="down" caption="needs dunning" :sparkline="[2,3,3,5,4,6,7]" sparkline-color="var(--h-danger)" />
      <HermesStatTile label="Churn (30d)" value="1.8%" :delta="-0.4" delta-text="0.4pp" good-direction="down" caption="improving" :sparkline="[2.6,2.4,2.3,2.1,2.0,1.9,1.8]" />
    </div>
    <p class="kit__note">Auto-compact: <code>{{ compactSamples }}</code></p>

    <!-- Status badges -->
    <h2 class="kit__sec">Status badges</h2>
    <div class="kit__row">
      <HermesStatusBadge v-for="s in statuses" :key="s" :status="s" />
    </div>

    <!-- Money cells -->
    <h2 class="kit__sec">Money cells</h2>
    <div class="kit__row kit__row--money">
      <HermesMoneyCell :amount="49" />
      <HermesMoneyCell :amount="2400" />
      <HermesMoneyCell :amount="1176.5" currency="USD" />
      <HermesMoneyCell :amount="null" />
    </div>

    <!-- API key reveal -->
    <h2 class="kit__sec">API key reveal field</h2>
    <div class="kit__card">
      <HermesApiKeyRevealField value="hk_live_9Fq2Xr7dP4nZ8sV1bT6wQ0m" />
    </div>
    <div class="kit__card" style="margin-top:12px">
      <HermesApiKeyRevealField value="hermes_live_7fQ0aB2cD4eF6gH8jK1mN3pQ" label="Activation token" :shown-once="false" hint="Rotate if exposed. Regenerate to invalidate the current token." />
    </div>

    <!-- Wizard steppers -->
    <h2 class="kit__sec">Wizard stepper — horizontal</h2>
    <div class="kit__card">
      <HermesWizardStepper :steps="wizardSteps" :current="2" @step-click="onStepClick" />
    </div>
    <h2 class="kit__sec">Wizard stepper — vertical checklist</h2>
    <HermesWizardStepper variant="vertical" :steps="checklist" :current="2" />

    <!-- Chart -->
    <h2 class="kit__sec">Chart (hermes echarts theme)</h2>
    <div class="kit__card">
      <HermesChart :option="chartOption" :height="300" aria-label="MRR rose from £31.2K in January to £48.2K in July 2026">
        <template #table>
          <table>
            <caption>MRR by month (£K)</caption>
            <tr v-for="(m, i) in chartMonths" :key="m"><th>{{ m }}</th><td>{{ chartValues[i] }}</td></tr>
          </table>
        </template>
      </HermesChart>
    </div>

    <!-- Empty states -->
    <h2 class="kit__sec">Empty states</h2>
    <div class="kit__grid kit__grid--2">
      <HermesEmptyState variant="first-run" title="Add your first product" message="Products are what you sell — each gets licence-key templates, plans and usage metrics.">
        <template #actions>
          <button class="kit__btn kit__btn--primary">New product</button>
          <button class="kit__btn kit__btn--secondary">Read the guide</button>
        </template>
      </HermesEmptyState>
      <HermesEmptyState variant="no-results" title="No subscriptions match" message="No results for status grace_period on plan “Enterprise”. Try widening the filter.">
        <template #actions>
          <button class="kit__btn kit__btn--secondary">Clear filters</button>
        </template>
      </HermesEmptyState>
      <HermesEmptyState variant="error" title="Couldn't load usage events" message="The aggregation service returned a 503. Your data is safe — this is a transient read error." @retry="onRetry" />
      <HermesEmptyState variant="loading" title="Loading invoices" />
    </div>

    <!-- Overlays -->
    <h2 class="kit__sec">Overlays</h2>
    <div class="kit__row">
      <button class="kit__btn kit__btn--secondary" @click="drawerOpen = true">Open drawer</button>
      <button class="kit__btn kit__btn--danger" @click="modalOpen = true">Open confirm modal</button>
      <span v-if="lastAction" class="kit__note">Last: {{ lastAction }}</span>
    </div>

    <HermesDrawer v-model="drawerOpen" title="Subscription #1042">
      <div style="margin-bottom:14px"><HermesStatusBadge status="active" /></div>
      <div class="kit__kv"><span>Customer</span><span class="kit__mono">nora@brightloom.io</span></div>
      <div class="kit__kv"><span>Product · Plan</span><span class="kit__mono">Daedalus · Team</span></div>
      <div class="kit__kv"><span>Price</span><HermesMoneyCell :amount="49" /></div>
      <div class="kit__kv"><span>Renews</span><span class="kit__mono">14 Aug 2026</span></div>
      <template #footer>
        <button class="kit__btn kit__btn--secondary" @click="drawerOpen = false">Close</button>
      </template>
    </HermesDrawer>

    <HermesConfirmModal
      v-model="modalOpen"
      title="Cancel subscription"
      description="This cancels Corvid Systems' Daedalus subscription immediately. Their install fails its next heartbeat."
      confirm-word="CANCEL"
      confirm-label="Cancel subscription"
      @confirm="onConfirm"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
// Dev-only preview harness for the Phase 0 shared kit. Not wired into any real
// route — HermesDashboard mounts it behind a `?kit=1` query (`/?kit=1`) for
// manual verification. Remove/ignore in production.
export default {
  name: 'HermesKitPreview',
  data() {
    return {
      crumbs: [
        { label: 'Design system', href: '#' },
        { label: 'Phase 0 kit' },
      ],
      statuses: ['active', 'trialing', 'past_due', 'grace_period', 'pending', 'cancelled', 'expired', 'offline'],
      wizardSteps: [
        { key: 'register', label: 'Register', hint: 'Daedalus' },
        { key: 'apikey', label: 'API key', hint: 'Credentials' },
        { key: 'test', label: 'Test connection', hint: 'Handshake' },
        { key: 'webhooks', label: 'Webhooks', hint: 'Endpoint' },
        { key: 'gateways', label: 'Gateways', hint: 'Validate' },
        { key: 'golive', label: 'Go live', hint: 'Activate' },
      ],
      checklist: [
        { label: 'Product registered', description: 'Daedalus · slug <span class="mono">daedalus</span> · requires activation' },
        { label: 'Live API key issued', description: '<span class="mono">hk_live_9Fq2…wQ0m</span> · created 15 Jul 2026' },
        { label: 'Webhook endpoint reachable', description: 'Sending test <span class="mono">subscription.created</span> event…' },
        { label: 'First heartbeat received', description: 'Waiting for the product to call <span class="mono">/licence/heartbeat</span>' },
      ],
      chartMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
      chartValues: [31.2, 34.5, 36.1, 39.8, 42.4, 45.6, 48.2],
      drawerOpen: false,
      modalOpen: false,
      lastAction: '',
    };
  },
  computed: {
    compactSamples() {
      return '1284 → 1.3K · 4,200,000 → 4.2M';
    },
    chartOption() {
      return {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', boundaryGap: false, data: this.chartMonths },
        yAxis: { type: 'value', axisLabel: { formatter: '£{value}K' } },
        series: [{
          name: 'MRR',
          type: 'line',
          areaStyle: { opacity: 0.1 },
          data: this.chartValues,
        }],
      };
    },
  },
  methods: {
    onRetry() { this.lastAction = 'retry emitted'; },
    onStepClick(i) { this.lastAction = `step-click ${i}`; },
    onConfirm() { this.lastAction = 'confirm (typed CANCEL)'; },
    onCancel() { this.lastAction = 'modal cancelled'; },
  },
};
</script>

<style scoped>
.kit { padding: 28px 32px 80px; max-width: 1200px; margin: 0 auto; font-family: var(--h-font-body, system-ui); color: var(--h-text-primary); background: var(--h-bg-content); min-height: 100vh; }
.kit__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 20px; }
.kit__eyebrow { font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--h-accent); }
.kit__title { font-size: 26px; font-weight: 600; letter-spacing: -0.02em; margin: 6px 0 8px; }
.kit__sub { color: var(--h-text-secondary); font-size: 14px; max-width: 70ch; margin: 0; }
.kit__sub code, .kit__note code { font-family: var(--h-font-mono); background: var(--h-bg-subtle); padding: 1px 6px; border-radius: 4px; font-size: 0.85em; }
.kit__chrome { display: flex; align-items: center; gap: 12px; }
.kit__sec { font-size: 13px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--h-text-secondary); margin: 36px 0 16px; padding-bottom: 8px; border-bottom: 1px solid var(--h-border); }
.kit__grid { display: grid; gap: 16px; }
.kit__grid--4 { grid-template-columns: repeat(4, 1fr); }
.kit__grid--2 { grid-template-columns: repeat(2, 1fr); }
@media (max-width: 900px) { .kit__grid--4, .kit__grid--2 { grid-template-columns: 1fr 1fr; } }
.kit__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.kit__row--money { gap: 28px; }
.kit__card { background: var(--h-bg-card); border: 1px solid var(--h-border); border-radius: var(--h-radius-lg); box-shadow: var(--h-shadow-card); padding: 20px 22px; }
.kit__note { font-size: 12.5px; color: var(--h-text-tertiary); margin-top: 10px; }
.kit__kv { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-bottom: 1px solid var(--h-border); font-size: 13px; }
.kit__kv:last-child { border-bottom: none; }
.kit__kv > span:first-child { color: var(--h-text-secondary); }
.kit__mono { font-family: var(--h-font-mono); font-size: 12.5px; font-weight: 500; }
.kit__btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius); border: 1px solid transparent; cursor: pointer; }
.kit__btn--primary { background: var(--h-accent); color: #fff; }
.kit__btn--secondary { background: var(--h-bg-card); color: var(--h-text-primary); border-color: var(--h-border-strong); }
.kit__btn--danger { background: var(--h-danger); color: #fff; }
</style>
